import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ErrorBanner, Spinner } from '../../../Admin/shared';
import { useReferralList } from '../api/useReferralList';
import { fmtAvgOut } from '../format';
import type { ReferralParty, ReferralRow, ReferralSortKey, ReferredActivity } from '../api/types';
import {
  EMPTY_REFERRAL_FILTERS,
  ENTERED_VIA_LABELS,
  ReferralFilters,
  STATUS_LABELS,
  type ReferralFilterState,
} from './ReferralFilters';

const PAGE_SIZE = 25;

interface ColumnDef {
  key: string;
  label: string;
  /** Present only on columns the backend can sort by (`ReferralSortKey`).
   *  "Referrer" and "referred" are not sortable — they're nested party
   *  objects, not scalar columns the API accepts a sort key for. */
  sortKey?: ReferralSortKey;
  align?: 'left' | 'right';
}

const COLUMNS: ColumnDef[] = [
  { key: 'when', label: 'When', sortKey: 'counted_at', align: 'left' },
  { key: 'referrer', label: 'Referrer', align: 'left' },
  { key: 'referred', label: 'Referred', align: 'left' },
  { key: 'code', label: 'Code', sortKey: 'code', align: 'left' },
  { key: 'entry', label: 'Entry', sortKey: 'entered_via', align: 'left' },
  { key: 'channel', label: 'Channel', sortKey: 'invite_channel', align: 'left' },
  { key: 'status', label: 'Status', sortKey: 'status', align: 'left' },
  { key: 'visits', label: 'Visits', sortKey: 'visits', align: 'right' },
  { key: 'avg_out', label: 'Avg time out', sortKey: 'minutes_out', align: 'right' },
  { key: 'friends', label: 'Friends', sortKey: 'friends', align: 'right' },
  { key: 'last_seen', label: 'Last seen', sortKey: 'last_seen', align: 'left' },
];

// The last four columns above are all activity-derived. When `activity` is
// null (the referred account was deleted) there is nobody to report on, so
// the row renders one merged "account deleted" cell across all of them
// rather than repeating the phrase four times.
const ACTIVITY_COLUMN_COUNT = 4;

/** `counted_at` is required on every row, but guard against an unparsable
 *  value rather than rendering "Invalid Date". */
function fmtWhen(v: string): string {
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

/**
 * `null` means the account was DELETED (both `referrer_id` and `referred_id`
 * are ON DELETE SET NULL) — render "deleted account", never a blank cell or
 * an invented name. A present party can still have a null `display_name`;
 * fall back to the user id, never a blank.
 */
function partyLabel(party: ReferralParty): string {
  if (party === null) return 'deleted account';
  return party.display_name ?? party.user_id;
}

/**
 * A null `invite_channel` means the channel was not recorded — distinct from
 * "no source." Settings-entered referrals ALWAYS have a null channel by
 * design (that redemption path never captures one), so those rows are
 * labeled distinctly rather than reading as a data-quality gap.
 */
function channelCell(row: ReferralRow): string {
  if (row.invite_channel !== null) return row.invite_channel;
  return row.entered_via === 'settings' ? 'Not recorded (settings entry)' : 'Not recorded';
}

function entryLabel(entered_via: ReferralRow['entered_via']): string {
  return ENTERED_VIA_LABELS[entered_via] ?? entered_via;
}

function statusLabel(status: string): string {
  return STATUS_LABELS[status] ?? status;
}

/** `visits` is a plain fact — 0 means a real person who never showed up, and
 *  must render as `0`, never blank or dashed. The verified count rides along
 *  as a tooltip rather than a second column. */
function visitsTitle(activity: ReferredActivity): string {
  return `${activity.visits_verified.toLocaleString()} verified`;
}

/** Recency only. `last_seen_at` is overwritten on every app open, so there is
 *  no count or frequency to show alongside it — a bare date, nothing more. */
function fmtLastSeen(v: string | null): string {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

function SortArrow({ active, order }: { active: boolean; order: 'asc' | 'desc' }): React.ReactElement {
  if (!active) {
    return <span className="ml-1 text-gray-700 group-hover:text-gray-500">↕</span>;
  }
  return <span className="ml-1 text-violet-400">{order === 'asc' ? '▲' : '▼'}</span>;
}

export interface ReferralListProps {
  from: string | null;
  to: string | null;
}

/**
 * The referrals list: the primary drill-down view of the referral funnel,
 * one row per referral attribution with its referrer, referred user, code,
 * entry point, channel and status. Filters, sort and paging are all
 * server-side — this component only tracks what it currently wants and lets
 * `useReferralList` fetch that slice, matching the friends page's `UserTable`
 * pattern rather than inventing a second paging idiom.
 */
export function ReferralList({ from, to }: ReferralListProps): React.ReactElement {
  const [filters, setFilters] = useState<ReferralFilterState>(EMPTY_REFERRAL_FILTERS);
  const [sort, setSort] = useState<ReferralSortKey>('counted_at');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);
  const [knownChannels, setKnownChannels] = useState<string[]>([]);

  // A date-range change narrows the result set exactly the way a filter change
  // does, so it has to reset paging for the same reason (see
  // `handleFiltersChange`). `from`/`to` are PROPS, though, so there is no
  // handler here to hang that off — without this the admin who is on page 6
  // and picks "Last 7 days" keeps `offset: 125`, gets zero rows back with
  // `total: 12`, and sees "No referrals in this period." above a "Showing
  // 126–12 of 12" footer with the pager gone (`totalPages` is 1), i.e. no way
  // back at all.
  //
  // Adjusted during render rather than in an effect (React's documented
  // "adjusting state when a prop changes" pattern): React re-runs this
  // component immediately with the corrected page, BEFORE it commits, renders
  // children or fires any effect, so `useReferralList` never issues a request
  // at the stale offset. An effect would fetch twice on every date change —
  // once at the stale offset, once corrected — and paint the broken state in
  // between. `currentPage` is what the rest of the render reads, so even the
  // discarded pass is self-consistent rather than briefly describing page 6.
  const dateKey = `${from ?? ''}|${to ?? ''}`;
  const [lastDateKey, setLastDateKey] = useState(dateKey);
  let currentPage = page;
  if (lastDateKey !== dateKey) {
    setLastDateKey(dateKey);
    setPage(0);
    currentPage = 0;
  }

  const { data, loading, error } = useReferralList({
    from: from ?? undefined,
    to: to ?? undefined,
    entered_via: filters.enteredVia || undefined,
    status: filters.status || undefined,
    channel: filters.channel || undefined,
    code: filters.code || undefined,
    q: filters.q || undefined,
    never_visited: filters.neverVisited || undefined,
    sort,
    order,
    limit: PAGE_SIZE,
    offset: currentPage * PAGE_SIZE,
  });

  // Channel is free-form (migration V159), so the filter's autocomplete list
  // is built from channels actually seen in the data rather than a
  // hardcoded enum that would hide new ones. Accumulated across loads so
  // browsing pages/filters doesn't shrink the list back down.
  useEffect(() => {
    if (!data) return;
    const seen = new Set<string>();
    for (const row of data.referrals) {
      if (row.invite_channel) seen.add(row.invite_channel);
    }
    if (seen.size === 0) return;
    setKnownChannels((prev) => {
      const merged = new Set(prev);
      let changed = false;
      for (const c of seen) {
        if (!merged.has(c)) {
          merged.add(c);
          changed = true;
        }
      }
      return changed ? Array.from(merged).sort() : prev;
    });
  }, [data]);

  // Takes an UPDATER, not a value: a debounced control in `ReferralFilters`
  // commits up to 400ms after the keystroke that scheduled it, by which time
  // the immediate controls (the Never-visited checkbox, Status, Channel) may
  // have moved on. A value built from the filter state captured when the timer
  // was scheduled would silently revert them.
  const handleFiltersChange = useCallback((update: (prev: ReferralFilterState) => ReferralFilterState) => {
    setFilters(update);
    // Any filter/search change can shrink the result set — always land back
    // on page 0 so a stale offset never renders a blank "page 4 of 2".
    setPage(0);
  }, []);

  const handleSort = useCallback(
    (key: ReferralSortKey) => {
      setPage(0);
      // Compute the next sort/order from CURRENT state up front and call
      // each setter with a plain value — never an updater function with a
      // side effect. React StrictMode double-invokes state updaters in
      // development, so a setOrder nested inside setSort's updater would
      // toggle order twice per click and cancel itself out (see UserTable).
      const nextOrder = key === sort ? (order === 'asc' ? 'desc' : 'asc') : 'desc';
      setSort(key);
      setOrder(nextOrder);
    },
    [sort, order],
  );

  const referrals = data?.referrals ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const hasActiveFilter =
    filters.enteredVia || filters.status || filters.channel || filters.code || filters.q || filters.neverVisited;

  return (
    <section className="rounded-2xl border border-gray-700 bg-gray-900 overflow-hidden">
      <div className="px-5 py-4 border-b border-gray-700 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-sm font-semibold text-gray-200">Referrals</h2>
          <p className="mt-1 text-[11px] text-gray-500">
            Every referral attribution in the selected window.
          </p>
        </div>
        <ReferralFilters value={filters} onChange={handleFiltersChange} knownChannels={knownChannels} />
      </div>

      {error && (
        <div className="p-4">
          <ErrorBanner message={error} />
        </div>
      )}

      {loading && !data && (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      )}

      {data && (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead>
                <tr className="border-b border-gray-700">
                  {COLUMNS.map((col) => {
                    const sortable = col.sortKey !== undefined;
                    const active = sortable && sort === col.sortKey;
                    return (
                      <th
                        key={col.key}
                        onClick={sortable ? () => handleSort(col.sortKey as ReferralSortKey) : undefined}
                        className={`px-5 py-3 font-semibold text-[11px] uppercase tracking-wider whitespace-nowrap select-none ${
                          col.align === 'right' ? 'text-right' : 'text-left'
                        } ${sortable ? 'group cursor-pointer' : ''} ${
                          active ? 'text-violet-300' : sortable ? 'text-gray-500 hover:text-gray-300' : 'text-gray-500'
                        }`}
                      >
                        {col.label}
                        {sortable && <SortArrow active={active} order={order} />}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {referrals.length === 0 && (
                  <tr>
                    <td colSpan={COLUMNS.length} className="px-5 py-10 text-center text-gray-500">
                      {hasActiveFilter ? 'No referrals match these filters.' : 'No referrals in this period.'}
                    </td>
                  </tr>
                )}
                {referrals.map((row) => (
                  <tr
                    key={row.id}
                    className="border-b border-gray-800 last:border-b-0 hover:bg-gray-800/40 transition-colors"
                  >
                    <td className="px-5 py-3 text-gray-400 whitespace-nowrap">{fmtWhen(row.counted_at)}</td>
                    <td className="px-5 py-3">
                      {row.referrer === null ? (
                        <span className="italic text-gray-500">deleted account</span>
                      ) : (
                        <Link
                          to={`/analytics/referrals/users/${row.referrer.user_id}`}
                          className="text-gray-200 hover:text-violet-300 transition-colors font-medium"
                        >
                          {partyLabel(row.referrer)}
                        </Link>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      {row.referred === null ? (
                        <span className="italic text-gray-500">deleted account</span>
                      ) : (
                        <Link
                          to={`/analytics/referrals/referred/${row.referred.user_id}`}
                          className="text-gray-200 hover:text-violet-300 transition-colors"
                        >
                          {partyLabel(row.referred)}
                        </Link>
                      )}
                    </td>
                    <td className="px-5 py-3 text-gray-400">{row.code ?? '—'}</td>
                    <td className="px-5 py-3 text-gray-400">{entryLabel(row.entered_via)}</td>
                    <td className="px-5 py-3 text-gray-400">{channelCell(row)}</td>
                    <td className="px-5 py-3 text-gray-400">{statusLabel(row.status)}</td>
                    {row.activity === null ? (
                      <td
                        colSpan={ACTIVITY_COLUMN_COUNT}
                        className="px-5 py-3 text-center italic text-gray-500"
                      >
                        account deleted
                      </td>
                    ) : (
                      <>
                        <td
                          className="px-5 py-3 text-right tabular-nums text-gray-200"
                          title={visitsTitle(row.activity)}
                        >
                          {row.activity.visits.toLocaleString()}
                        </td>
                        <td className="px-5 py-3 text-right tabular-nums text-gray-400">
                          {fmtAvgOut(row.activity.minutes_out_avg)}
                        </td>
                        <td className="px-5 py-3 text-right tabular-nums text-gray-400">
                          {row.activity.friends.toLocaleString()}
                        </td>
                        <td className="px-5 py-3 text-gray-400 whitespace-nowrap">
                          {fmtLastSeen(row.activity.last_seen_at)}
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {total > 0 && (
            <div className="flex items-center justify-between px-5 py-3 border-t border-gray-700 flex-wrap gap-2">
              <p className="text-[11px] text-gray-500">
                Showing {currentPage * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE + referrals.length, total)}{' '}
                of {total.toLocaleString()}
              </p>
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(0, p - 1))}
                    disabled={currentPage === 0}
                    className="px-2.5 py-1 text-[11px] font-medium text-gray-300 bg-gray-950 border border-gray-700 rounded-md hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    ← Prev
                  </button>
                  <span className="text-[11px] text-gray-500 tabular-nums">
                    Page {currentPage + 1} of {totalPages}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                    disabled={currentPage >= totalPages - 1}
                    className="px-2.5 py-1 text-[11px] font-medium text-gray-300 bg-gray-950 border border-gray-700 rounded-md hover:text-white disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Next →
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}

export default ReferralList;
